import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
import { navigate } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import React, { useEffect } from 'react'
import { LinkButtonProp, LinkButtonRegisterProps } from '../common/button'

import './index.css'

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}
enum Widths {
  Full = 'full',
  Part = 'part'
}
interface Props {
  backgroundColor: string
  titleOne: string
  titleColor: string
  titleOneBold: string
  textAlignment: Alignment
  textWidth: Widths
  contentText: string
  treatButtonEnabled: boolean
  accountButtonEnabled: boolean
  buttonAlignment: Alignment
  treatButtonProp: LinkButtonProp
  accountButtonProp: LinkButtonProp
  mediaBackdropColor: string
  builderHeroImage: any
  builderHeroMobileImage: any
  cloudinaryHeroImageAltText: string
  cloudinaryHeroImageContain: boolean
  mobileImageFirst: boolean
  showWidget: boolean
  showWidgetImage: boolean
  widgetImage: string
  widgetBackgroundColor: string
  widgetType: WidgetType
  widgetTags: string
  showRating: boolean
  ratingTextBold: string
  ratingTextNormalOne: string
  ratingTextNormalTwo: string
  builderRatingIcon: string
  heroFooterBackground: string
  footerText: string
  footerTextColor: string
  headingType: HeadingType
  headingClass: HeadingClass
}

const HeroTreatsCTA = ({
  backgroundColor,
  titleOne,
  titleColor,
  titleOneBold,
  textAlignment,
  textWidth,
  contentText,
  treatButtonEnabled,
  treatButtonProp,
  buttonAlignment,
  mediaBackdropColor,
  builderHeroImage,
  builderHeroMobileImage,
  cloudinaryHeroImageAltText,
  cloudinaryHeroImageContain,
  mobileImageFirst,
  showWidget,
  showWidgetImage,
  widgetImage,
  widgetBackgroundColor,
  widgetType,
  widgetTags,
  showRating,
  ratingTextBold,
  ratingTextNormalOne,
  ratingTextNormalTwo,
  builderRatingIcon,
  heroFooterBackground
}: Props) => {
  useEffect(() => {
    const trustWidget = document.getElementById('trust-pilot-micro-combo')
    window.Trustpilot?.loadFromElement(trustWidget)
  }, [widgetType])

  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const getWidthClass = () => {
    if (textWidth === Widths.Full) {
      return 'w-full px-4'
    }

    return 'w-4/5 pl-16'
  }

  const getJustifyAlignmentClass = () => {
    if (buttonAlignment === Alignment.Right) {
      return 'ml-auto'
    }
    if (buttonAlignment === Alignment.Center) {
      return 'mx-auto'
    }
    return 'mr-auto lg:ml-16'
  }

  const renderMobileBanner = () => {
    if (builderHeroMobileImage) {
      return (
        <CdnImage
          className={`index-2 relative h-full min-h-[11rem] ${cloudinaryHeroImageContain ? 'object-contain w-2/3 mx-auto flex justify-center items-center' : 'object-cover w-full'} lg:hidden`}
          url={builderHeroMobileImage}
          alt={cloudinaryHeroImageAltText}
          steps={[600, 1080]}
          width="auto"
          lazyLoad={false}
        />
      )
    }
  }

  const renderImage = () => {
    return (
      <CdnImage
        className={`index-2 relative w-full ${cloudinaryHeroImageContain ? 'object-contain' : 'object-cover'} max-h-[28rem] h-[21.5rem] rounded-[30px]`}
        lazyLoad={false}
        url={builderHeroImage}
        alt={cloudinaryHeroImageAltText}
        steps={[1080]}
        width={'500'}
      />
    )
  }

  const renderHeroText = () => {
    return (
      <div>
        <div className={`text-center lg:${getAlignmentClass()} pb-2 lg:pb-4`}>
          <span className="text-4xl lg:text-6xl font-medium font-serif" style={{ color: titleColor }}>
            {titleOne}
          </span>
          <br />
          <div />
          <span className="text-teal-900 text-2xl lg:text-4xl font-medium font-serif"> {titleOneBold}</span>
        </div>
        <p
          className={`enable-list-styles text-xs md:text-sm font-medium font-sans w-full text-center lg:text-left mt-4 lg:mt-0`}
          dangerouslySetInnerHTML={{ __html: contentText }}
        ></p>{' '}
      </div>
    )
  }

  const renderCTATreatButton = () => {
    if (!treatButtonEnabled) {
      return (
        <div className="my-2 mx-auto w-auto lg:w-3/5 mt-8">
          <button
            className={`flex w-10/12 items-center justify-center mx-auto lg:${getJustifyAlignmentClass()} rounded-full border border-transparent bg-[#006685] px-5 py-3 text-base font-medium text-white md:mt-8 md:w-96 md:text-lg ${cloudinaryHeroImageContain ? 'lg:my-8' : 'lg:my-16'} lg:w-3/5`}
            onClick={() => {
              if (typeof window.gtag !== 'undefined') {
                window.gtag('event', 'cta_component_dog_hero_treat_cta_logged_in', {
                  page: 'home_page'
                })
              }
              navigate(`${treatButtonProp?.buttonUrl}`)
            }}
          >
            {`${treatButtonProp?.buttonText}`}
          </button>
        </div>
      )
    }

    return null
  }

  const getWidget = () => {
    if (showWidget) {
      return (
        <div className="flex justify-center lg:justify-start pt-1 lg:pl-16">
          {/* <!-- TrustBox widget - Micro Combo --> */}
          <div
            className="trustpilot-widget flex justify-center lg:justify-start"
            data-locale="en-GB"
            data-template-id="5419b6ffb0d04a076446a9af"
            data-businessunit-id="5af423d4a3303b0001bf47c1"
            data-style-height="20px"
            data-style-width="100%"
            data-theme="light"
            data-stars="5"
            data-font-family="Montserrat"
            style={{ backgroundColor }}
            data-text-color="#004355"
            data-tags={widgetTags ? widgetTags : ''}
            id="trust-pilot-micro-combo"
          >
            <a href="https://uk.trustpilot.com/review/differentdog.com" target="_blank" rel="noreferrer noopener">
              <StaticImage
                objectFit="contain"
                className="w-full "
                src="../../../images/trustpilot.png"
                alt="Trustpilot"
              />
            </a>
          </div>
          {/* <!-- End TrustBox widget --> */}
        </div>
      )
    }
  }

  const getWidgetImage = () => {
    return (
      <div className="flex justify-center lg:justify-start pt-1 lg:pl-16">
        <a href="https://uk.trustpilot.com/review/differentdog.com" target="_blank" rel="noreferrer noopener">
          <img className="w-5/6 mx-auto lg:w-full object-contain" src={widgetImage} alt="Trustpilot" />
        </a>
      </div>
    )
  }

  const renderRatingIcon = () => {
    return (
      <CdnImage
        className="index-2 relative inline-block h-3 w-3 "
        url={builderRatingIcon}
        alt={'Dog bowl'}
        lazyLoad={false}
      />
    )
  }

  return (
    <div
      className="bg-image-none relative mb-0 lg:-mb-4 lg:-mt-12 h-full w-full md:bg-cover md:bg-bottom md:bg-no-repeat"
      style={{ backgroundColor }}
    >
      <main
        className={`mx-auto h-full w-full flex ${mobileImageFirst ? 'flex-col-reverse -mt-4 md:-mt-12' : 'flex-col'} lg:container mb-8 lg:-mb-0 lg:flex-row justify-center items-center xl:w-4/5`}
      >
        {/* Mobile view */}
        <div className="block lg:hidden">
          <div className={`${cloudinaryHeroImageContain ? 'mt-6' : '-mt-4'} block h-full min-h-[11rem] lg:hidden`}>
            {renderMobileBanner()}
          </div>
          <div className="mx-auto h-full max-h-[500px] min-h-[8rem] w-4/5 justify-center pt-4 md:pt-8 md:w-2/3 font-medium font-sans">
            {renderHeroText()}
          </div>
          {showWidget && (
            <div className="w-full pt-6" style={{ backgroundColor: widgetBackgroundColor }}>
              {getWidget()}
              {getWidgetImage()}
            </div>
          )}
          {showRating && (
            <div className="text-center text-xs lg:text-base pt-6 lg:pl-16">
              <span className="font-bold">{ratingTextBold} </span>
              <span>
                {ratingTextNormalOne} {renderRatingIcon()}
              </span>
              <span className="font-bold"> {ratingTextNormalTwo} </span>
            </div>
          )}
          {renderCTATreatButton()}
        </div>
        {/* Desktop view */}
        <div className="hidden lg:block w-full">
          <div className="flex flex-row mx-auto h-full w-full lg:container">
            <div className="flex flex-col w-full mx-auto h-full min-h-[15rem] justify-center pt-24 max-h-[1000px]">
              <div className={`text-left font-medium font-sans ${getWidthClass()}`}> {renderHeroText()}</div>
              {showWidget && (
                <div className="w-full pt-10" style={{ backgroundColor: widgetBackgroundColor }}>
                  {getWidget()}
                  {getWidgetImage()}
                </div>
              )}
              {showRating && (
                <div className="text-left text-xs lg:text-base pt-10 pb-4 pl-16">
                  <span className="font-bold">{ratingTextBold} </span>
                  <span>
                    {ratingTextNormalOne} {renderRatingIcon()}
                  </span>
                  <span className="font-bold"> {ratingTextNormalTwo} </span>
                </div>
              )}
              <div>{renderCTATreatButton()}</div>
            </div>
            <div className="mt-12 block w-full pt-4">
              <div className="max-h-22 relative mx-auto ml-5 h-22 w-5/6 rounded-[30px]">
                <div
                  className="image-backdrop-square-wrapper absolute h-22 w-full rounded-[30px]"
                  style={{ backgroundColor: mediaBackdropColor }}
                />
                {renderImage()}
              </div>
            </div>
          </div>
        </div>
      </main>
      <div
        className="relative w-full bg-cover bg-top bg-no-repeat h-10 mb:4 lg:h-28 md:mb-4 xl:h-32 2xl:h-[12rem] lg:mt-4 -mt-6"
        style={{
          backgroundColor: 'FFFFFF',
          backgroundImage: heroFooterBackground ? `url(${heroFooterBackground})` : 'none'
        }}
      ></div>
    </div>
  )
}

export default HeroTreatsCTA

Builder.registerComponent(HeroTreatsCTA, {
  name: 'HeroTreatsCTA',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'titleOne',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'titleColor',
      type: 'color',
      defaultValue: '#006685'
    },
    {
      name: 'titleOneBold',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    {
      name: 'textWidth',
      type: 'string',
      enum: ['full', 'part'],
      defaultValue: 'part'
    },
    {
      name: 'contentText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'builderHeroImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'builderHeroMobileImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },

    {
      name: 'cloudinaryHeroImageAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'cloudinaryHeroImageContain',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'mobileImageFirst',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'mediaBackdropColor',
      type: 'color',
      defaultValue: 'cyan'
    },
    {
      name: 'showWidget',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'showWidgetImage',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'widgetImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'widgetBackgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'widgetType',
      type: 'string',
      enum: ['carousel', 'microCombo', 'list'],
      defaultValue: 'microCombo'
    },
    {
      name: 'widgetTags',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'showRating',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'ratingTextBold',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'ratingTextNormalOne',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'ratingTextNormalTwo',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'builderRatingIcon',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'treatButtonEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      ...LinkButtonRegisterProps,
      name: 'treatButtonProp'
    },
    {
      name: 'buttonAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    {
      name: 'heroFooterBackground',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    }
  ]
})
